/**
 * React Query hooks for Authentication
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authService, apiClient, queryKeys, toUser, type User } from '@/lib';
import type { components } from '@/types/api.generated';
import type { LoginFormData, RegisterFormData, ProfileFormData, ChangePasswordFormData } from '@/lib/validations';

// The User shape lives in lib/auth (mapped from the contract's UserResponse).
// A second, hand-written copy used to live here with a `username` field the
// API has never returned.
export type { User };

type UserResponse = components['schemas']['UserResponse'];

// ==========================================
// Query Hooks
// ==========================================

/**
 * Get current authenticated user
 */
export function useCurrentUser() {
  return useQuery({
    queryKey: queryKeys.user.profile,
    queryFn: async () => toUser(await apiClient.get<UserResponse>('/auth/me')),
    enabled: authService.isAuthenticated(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });
}

/**
 * Check if user is authenticated
 */
export function useAuth() {
  const { data: user, isLoading, error, refetch } = useCurrentUser();
  
  return {
    user,
    isLoading,
    error,
    isAuthenticated: authService.isAuthenticated() && !!user,
    refetch,
  };
}

// ==========================================
// Mutation Hooks
// ==========================================

/**
 * Login mutation
 */
export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: LoginFormData) =>
      authService.login({
        email: data.email,
        password: data.password,
        rememberMe: data.rememberMe,
      }),
    onSuccess: () => {
      // Refetch user profile
      queryClient.invalidateQueries({ queryKey: queryKeys.user.profile });
    },
  });
}

/**
 * Register mutation.
 *
 * The endpoint takes UserCreate (name/email/password) and answers with the
 * created user, not tokens — callers sign in afterwards. It is public, so no
 * Authorization header and no token refresh on the way out.
 */
export function useRegister() {
  return useMutation({
    mutationFn: ({ name, email, password }: RegisterFormData) =>
      apiClient.post<UserResponse>(
        '/auth/register',
        { name, email, password } satisfies components['schemas']['UserCreate'],
        { skipAuth: true }
      ),
  });
}

/**
 * Logout mutation
 */
export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await authService.logout();
    },
    onSuccess: () => {
      // Clear all queries
      queryClient.clear();
    },
  });
}

/**
 * Update profile mutation.
 *
 * Backend route is `PATCH /users/me` taking `UserUpdate{name, phone, avatar}` —
 * there is no `PUT /auth/profile`, so the old call 404'd every save. Email is
 * read-only on that model, so it is dropped here; an empty phone is omitted
 * rather than sent as "" (which fails the phone pattern with a 422).
 *
 * The header chip reads `authService.getUser()` from storage, not the query
 * cache, so the stored user is refreshed too or the name stays stale.
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ProfileFormData) => {
      const body: components['schemas']['UserUpdate'] = {
        name: data.name,
        phone: data.phone ? data.phone : undefined,
        avatar: data.avatar ? data.avatar : undefined,
      };
      return toUser(await apiClient.patch<UserResponse>('/users/me', body));
    },
    onSuccess: (newUser) => {
      queryClient.setQueryData(queryKeys.user.profile, newUser);
      authService.updateStoredUser(newUser);
    },
  });
}

/**
 * Change password mutation.
 *
 * Backend `PasswordChangeRequest` expects snake_case `current_password` /
 * `new_password`; sending camelCase made every request a 422 for missing
 * fields, which the settings page then mislabelled as a wrong password.
 */
export function useChangePassword() {
  return useMutation({
    mutationFn: (data: ChangePasswordFormData) =>
      apiClient.post<{ message: string }>('/auth/change-password', {
        current_password: data.currentPassword,
        new_password: data.newPassword,
      } satisfies components['schemas']['PasswordChangeRequest']),
  });
}

/**
 * Request password reset
 */
export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: (email: string) => 
      apiClient.post<{ message: string }>('/auth/forgot-password', { email }),
  });
}

/**
 * Reset password with token
 */
export function useResetPassword() {
  return useMutation({
    mutationFn: ({ token, password }: { token: string; password: string }) => 
      apiClient.post<{ message: string }>('/auth/reset-password', { token, password }),
  });
}

/**
 * Verify email with token
 */
export function useVerifyEmail() {
  return useMutation({
    mutationFn: (token: string) => 
      apiClient.post<{ message: string }>('/auth/verify-email', { token }),
  });
}

/**
 * Resend verification email
 */
export function useResendVerification() {
  return useMutation({
    mutationFn: () => 
      apiClient.post<{ message: string }>('/auth/resend-verification', {}),
  });
}
